import { userClient } from '../config/api';
import {
  ApiResponse,
  ProviderUser,
  CreateProviderUserRequest,
  UpdateProviderUserRequest,
  GetAllProviderUsersParams,
} from './types/user.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class ProviderUserService {
  private readonly basePath = '/api/users/providers';

  createProviderUser = async (data: CreateProviderUserRequest): Promise<ProviderUser> => {
    try {
      const response = await userClient.post<ApiResponse<ProviderUser>>(this.basePath, data);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error creating provider user');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error creating provider user');
    }
  }

  getAllProviderUsers = async (params?: GetAllProviderUsersParams): Promise<ProviderUser[]> => {
    try {
      const response = await userClient.get<ApiResponse<any>>(this.basePath, {
        params: {
          page: 1,
          pageSize: 1000,
          ...params
        },
      });

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching provider users');
      }

      const data = response.data.data;
      if (data && typeof data === 'object' && 'data' in data) {
        return data.data || [];
      }

      return data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching provider users');
    }
  }

  getProviderUserById = async (id: string): Promise<ProviderUser> => {
    try {
      const response = await userClient.get<ApiResponse<ProviderUser>>(`${this.basePath}/${id}`);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching provider user');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching provider user');
    }
  }

  updateProviderUser = async (id: string, data: UpdateProviderUserRequest): Promise<ProviderUser> => {
    try {
      const response = await userClient.put<ApiResponse<ProviderUser>>(
        `${this.basePath}/${id}`,
        data
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error updating provider user');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating provider user');
    }
  }

  deleteProviderUser = async (id: string): Promise<void> => {
    try {
      const response = await userClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting provider user');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting provider user');
    }
  }
}

export const providerUserService = new ProviderUserService();
